import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfPoint, PdfPen, PdfColor } from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/image';
import '../../pdf/draw/label';

/**
 * Hex - Hexagonal grid with images
 *
 * Layout: Dynamic number of landscape pages.
 * Each page: honeycomb pattern (rows of 4, 3, 4 = 11 hex positions, up to 10 items).
 * Empty hexagons not drawn.
 * Hexagon size fixed across all pages.
 *
 * Migrated from C# EduFrame.Img10.Generators.Hex
 */
export class Hex implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const r = (PdfSize.A4.width - 2 * margin) / 5;
    const d = (r * Math.sqrt(3)) / 2;

    const origin = new PdfPoint(
      (PdfSize.A4.height - 8 * d) / 2 + d,
      (PdfSize.A4.width - 5 * r) / 2 + r
    );

    const count = Math.min(opts.list.length, 50);
    const itemsPerPage = 10;
    const pageCount = Math.ceil(count / itemsPerPage);

    const bluePen = new PdfPen(PdfColor.fromRgb(135, 206, 250), 16);
    const cellSize = new PdfSize(d * 1.3, d * 1.3);

    for (let pageIdx = 0; pageIdx < pageCount; pageIdx++) {
      const page = pdf.addPage(true); // landscape
      const pageStartIdx = pageIdx * itemsPerPage;
      const pageEndIdx = Math.min(pageStartIdx + itemsPerPage, count);
      const itemsOnPage = pageEndIdx - pageStartIdx;

      const pageItems = opts.list.slice(pageStartIdx, pageEndIdx);
      const allTexts = pageItems.map((item) => [item.left.getText() ?? '']);
      const font = page.adjustFontMulti(allTexts, GUtil.baseFont, cellSize);

      let hexIndex = 0;
      for (let y = 0; y < 3; y++) {
        const cols = 4 - (1 - (y % 2));
        for (let x = 0; x < cols; x++) {
          const px = origin.x + (x * 2 + (1 - (y % 2))) * d;
          const py = origin.y + (r / 2) * 3 * y;
          const center = new PdfPoint(px, py);

          if (hexIndex < itemsOnPage) {
            this.drawHex(page, center, r, bluePen);
            const imgSize = d * 1.3;
            const imgRect = new PdfRect(px - imgSize / 2, py - imgSize / 2, imgSize, imgSize);
            page.drawLabel(pageItems[hexIndex].left, { rect: imgRect, font });
          }
          hexIndex++;
        }
      }
    }
  }

  private drawHex(page: any, center: PdfPoint, r: number, bluePen: PdfPen): void {
    const count = 6;
    const points: PdfPoint[] = [];
    for (let i = 0; i < count; i++) {
      points.push(
        new PdfPoint(
          center.x + r * Math.sin((i * Math.PI * 2) / count),
          center.y + r * Math.cos((i * Math.PI * 2) / count)
        )
      );
    }

    const closed = [...points, points[0]];
    page.drawLines(bluePen, closed);
    page.drawLines(GUtil.basePen, closed);
  }
}
